import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { X57_LOCAL_DEMO_PROJECT_ID } from "./x57VspApi";
import { requestAirshowReview, saveAirshowReviewFeedback } from "./airshowReviewApi";
import {
  AirshowComparisonViewer,
  type AirshowCameraPreset,
  type AirshowComparisonSide,
} from "./AirshowComparisonViewer";
import { AirshowJunctionLab } from "./AirshowJunctionLab";
import { AirshowHeartbeatPanel } from "./AirshowHeartbeatPanel";
import {
  AirshowReviewToolNavigation,
  type AirshowReviewTool,
} from "./AirshowReviewToolNavigation";
import type { AirshowJunctionFeedbackEvidence } from "./airshowJunctionFeedback";
import {
  airshowAlignmentSignature,
  type AirshowAlignmentMode,
  type AirshowCameraEvidence,
  type AirshowManualAlignment,
  type AirshowReviewLayout,
  type AirshowReviewResponse,
} from "./airshowReviewContract";
import {
  airshowFeedbackFilename,
  createAirshowReviewFeedback,
  serializeAirshowReviewFeedback,
} from "./airshowReviewFeedback";
import {
  loadBundledLoomAirshowPresetCatalog,
  type LoomAirshowCatalogState,
  type LoomAirshowPresetEntry,
} from "./loomAirshowPresets";
import "./airshow-review.css";

const DEFAULT_ENTRY_ID = "airshow/g8vqpxrbwhp3iyogwynm/fl52";

interface WorkbenchProps {
  projectId: string;
  projectName: string;
  branch: string;
  persistedProject: boolean;
}

type FeedbackStatus = {
  kind: "idle" | "saving" | "success" | "error";
  message: string;
};
type JunctionEvidenceState = {
  reviewIdentity: string;
  evidence: AirshowJunctionFeedbackEvidence;
};

function identityAlignment(): AirshowManualAlignment {
  return { translation: [0, 0, 0], rotationDegrees: [0, 0, 0], scale: 1 };
}

function unavailableReview(entry: LoomAirshowPresetEntry, message: string): AirshowReviewResponse {
  return {
    schema: "loom.airshow-review/v1",
    entry_id: entry.id,
    archive_member_path: entry.archiveMemberPath,
    source_sha256: entry.sourceAsset.sha256,
    source_frame: {
      frame_id: "openvsp-source-native", axes: ["x", "y", "z"],
      handedness: "right-handed", length_unit: "source-native-unbound",
    },
    alignment: {
      mode: "identity-source-frame",
      candidate_to_reference: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
    },
    reference: {
      status: "unavailable", representation: "unavailable", model_url: null,
      artifact_sha256: null, bbox: null, code: "review-unavailable", message,
      retryable: true, warnings: [message],
    },
    loom: {
      status: "unavailable", representation: "unavailable", fidelity: "unavailable",
      model_url: null, bundle_key: null, geometry_complete: false, skipped_surfaces: [],
      export_order_attestation: null, bbox: null, code: "review-unavailable", message,
      retryable: true, warnings: [message],
    },
    quantitative_comparison_allowed: false,
  };
}

function fidelityLabel(review: AirshowReviewResponse | null): string {
  if (!review || review.loom.status === "unavailable") return "Unavailable";
  if (review.loom.fidelity === "exact-attested") return "Exact controls · attested";
  if (review.loom.fidelity === "exact-unattested") return "Exact controls · unattested";
  return review.loom.representation === "sampled-grid" ? "Adapted · sampled grid" : "Adapted · tessellated";
}

function comparisonSides(
  entryId: string,
  review: AirshowReviewResponse | null,
  loading: boolean,
  error: string | null,
): { reference: AirshowComparisonSide; loom: AirshowComparisonSide } {
  if (!review) {
    const status = loading ? "loading" : "unavailable";
    return {
      reference: {
        modelId: entryId, status, geometryMode: "unavailable", modelUrl: null,
        fidelityCode: "unavailable", fidelityLabel: loading ? "Loading reference…" : "Unavailable",
        unavailableReason: error ?? "Historical reference unavailable",
      },
      loom: {
        modelId: entryId, status, geometryMode: "unavailable", modelUrl: null,
        fidelityCode: "unavailable", fidelityLabel: loading ? "Running adapter…" : "Unavailable",
        unavailableReason: error ?? "Loom OpenVSP adapter unavailable",
      },
    };
  }
  return {
    reference: review.reference.status === "available" ? {
      modelId: entryId, status: "available", geometryMode: "tessellated",
      modelUrl: review.reference.model_url, bbox: review.reference.bbox,
      fidelityCode: "historical-reference", fidelityLabel: "Historical X3D · unattested",
    } : {
      modelId: entryId, status: "unavailable", geometryMode: "unavailable", modelUrl: null,
      fidelityCode: "unavailable", fidelityLabel: "Unavailable",
      unavailableReason: review.reference.message,
    },
    loom: review.loom.status === "available" ? {
      modelId: entryId, status: "available", geometryMode: review.loom.representation,
      modelUrl: review.loom.model_url, bbox: review.loom.bbox,
      fidelityCode: review.loom.fidelity, fidelityLabel: fidelityLabel(review),
    } : {
      modelId: entryId, status: "unavailable", geometryMode: "unavailable", modelUrl: null,
      fidelityCode: "unavailable", fidelityLabel: "Unavailable",
      unavailableReason: review.loom.message,
    },
  };
}

function tupleWith(value: readonly [number, number, number], index: number, next: number) {
  return value.map((current, currentIndex) => currentIndex === index ? next : current) as [number, number, number];
}

export function AirshowReviewWorkbench({
  projectId, projectName, branch, persistedProject,
}: WorkbenchProps) {
  const [catalogState, setCatalogState] = useState<LoomAirshowCatalogState | null>(null);
  const [entryId, setEntryId] = useState(DEFAULT_ENTRY_ID);
  const [review, setReview] = useState<AirshowReviewResponse | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<{ entryId: string; message: string } | null>(null);
  const [layout, setLayout] = useState<AirshowReviewLayout>("split");
  const [overlayOpacity, setOverlayOpacity] = useState(0.55);
  const [visibility, setVisibility] = useState({ reference: true, loom: true });
  const [alignmentMode, setAlignmentMode] = useState<AirshowAlignmentMode>("source-frame");
  const [manualAlignment, setManualAlignment] = useState<AirshowManualAlignment>(identityAlignment);
  const [resolvedAlignment, setResolvedAlignment] = useState<AirshowManualAlignment>(identityAlignment);
  const [camera, setCamera] = useState<AirshowCameraEvidence>({ signature: "camera|pending", revision: 0 });
  const [cameraCommand, setCameraCommand] = useState<{ id: number; preset: AirshowCameraPreset }>({ id: 0, preset: "isometric" });
  const [reviewTool, setReviewTool] = useState<AirshowReviewTool>("compare");
  const [junctionEvidenceState, setJunctionEvidenceState] = useState<JunctionEvidenceState | null>(null);
  const [note, setNote] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState<FeedbackStatus>({ kind: "idle", message: "" });
  const requestSequence = useRef(0);
  const saveSequence = useRef(0);
  const saveController = useRef<AbortController | null>(null);
  const saveIdentityRef = useRef<string | null>(null);
  const catalog = catalogState?.kind === "available" ? catalogState.catalog : null;
  const selectedEntry = useMemo(
    () => catalog?.entries.find((entry) => entry.id === entryId) ?? null,
    [catalog, entryId],
  );
  const activeReview = review && selectedEntry
    && review.entry_id === selectedEntry.id
    && review.archive_member_path === selectedEntry.archiveMemberPath
    && review.source_sha256 === selectedEntry.sourceAsset.sha256 ? review : null;
  const activeError = reviewError && reviewError.entryId === selectedEntry?.id
    ? reviewError.message : null;
  const activeLoading = reviewLoading || Boolean(selectedEntry && !activeReview && !activeError);
  const reviewProjectId = !persistedProject && import.meta.env.DEV
    ? X57_LOCAL_DEMO_PROJECT_ID : projectId;
  const junctionIdentity = activeReview?.loom.status === "available" ? {
    entryId: activeReview.entry_id,
    archiveMemberPath: activeReview.archive_member_path,
    sourceSha256: activeReview.source_sha256.replace(/^sha256:/, ""),
    reviewBundleKey: activeReview.loom.bundle_key,
  } : null;
  const junctionReviewIdentity = junctionIdentity ? [
    junctionIdentity.entryId, junctionIdentity.archiveMemberPath,
    junctionIdentity.sourceSha256, junctionIdentity.reviewBundleKey,
  ].join("\u001f") : null;
  const junctionEvidence = reviewTool === "junction"
    && junctionEvidenceState?.reviewIdentity === junctionReviewIdentity
    ? junctionEvidenceState.evidence : null;

  useEffect(() => {
    let active = true;
    void loadBundledLoomAirshowPresetCatalog().then((state) => {
      if (!active) return;
      setCatalogState(state);
      if (state.kind === "available" && !state.catalog.entries.some(({ id }) => id === entryId)) {
        setEntryId(state.catalog.entries[0]?.id ?? "");
      }
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedEntry) return;
    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    setReview(null);
    setReviewError(null);
    setReviewLoading(true);
    void requestAirshowReview({
      projectId: reviewProjectId,
      branch,
      entry: {
        id: selectedEntry.id,
        archiveMemberPath: selectedEntry.archiveMemberPath,
        sourceSha256: selectedEntry.sourceAsset.sha256,
      },
      resolution: 8,
      signal: controller.signal,
    }).then((nextReview) => {
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      setReview(nextReview);
      setReviewLoading(false);
    }).catch((error: unknown) => {
      if (controller.signal.aborted || sequence !== requestSequence.current) return;
      setReviewError({
        entryId: selectedEntry.id,
        message: error instanceof Error ? error.message : "Airshow review is unavailable",
      });
      setReviewLoading(false);
    });
    return () => controller.abort();
  }, [branch, reviewProjectId, selectedEntry]);

  const updateResolvedAlignment = useCallback((next: AirshowManualAlignment) => {
    setResolvedAlignment((current) => airshowAlignmentSignature("manual", current)
      === airshowAlignmentSignature("manual", next) ? current : next);
  }, []);
  const alignmentSignature = airshowAlignmentSignature(alignmentMode, resolvedAlignment);
  const sides = comparisonSides(entryId, activeReview, activeLoading, activeError);
  const packetReview = selectedEntry
    ? activeReview ?? unavailableReview(selectedEntry, activeError ?? "Review geometry is loading") : null;
  const feedback = useMemo(() => selectedEntry && catalog && packetReview
    && (reviewTool !== "junction" || junctionEvidence)
    ? createAirshowReviewFeedback({
      catalog: { catalogId: catalog.catalogId, catalogRevision: catalog.catalogRevision },
      entry: {
        id: selectedEntry.id, label: selectedEntry.label,
        archiveMemberPath: selectedEntry.archiveMemberPath,
        sourceSha256: selectedEntry.sourceAsset.sha256,
      },
      review: packetReview,
      view: {
        layout, camera, overlayOpacity, visibility,
        alignment: { mode: alignmentMode, manual: manualAlignment, resolved: resolvedAlignment },
      },
      junction: junctionEvidence ?? undefined,
      note,
    }) : null, [alignmentMode, camera, catalog, layout, manualAlignment, note, overlayOpacity,
      junctionEvidence, packetReview, resolvedAlignment, reviewTool, selectedEntry, visibility]);
  const preview = feedback ? serializeAirshowReviewFeedback(feedback) : "Review evidence is loading.";
  const feedbackSaveIdentity = feedback && activeReview
    ? `${reviewProjectId}\u001f${preview}` : null;
  const canSaveFeedback = Boolean(feedbackSaveIdentity && note.trim()
    && (reviewTool !== "junction" || junctionEvidence));

  useEffect(() => {
    saveIdentityRef.current = feedbackSaveIdentity;
    saveSequence.current += 1;
    saveController.current?.abort();
    saveController.current = null;
    setFeedbackStatus({ kind: "idle", message: "" });
  }, [feedbackSaveIdentity]);

  useEffect(() => () => saveController.current?.abort(), []);

  const chooseCamera = (preset: AirshowCameraPreset) => {
    setCameraCommand((current) => ({ id: current.id + 1, preset }));
  };
  const chooseReviewTool = (tool: AirshowReviewTool) => {
    setJunctionEvidenceState(null);
    setReviewTool(tool);
  };
  const inspectHeartbeatModel = (modelId: string) => {
    if (!catalog?.entries.some((entry) => entry.id === modelId)) return;
    setEntryId(modelId);
    setJunctionEvidenceState(null);
    setReviewTool("compare");
  };
  const resetAlignment = () => {
    const identity = identityAlignment();
    setAlignmentMode("source-frame");
    setManualAlignment(identity);
    setResolvedAlignment(identity);
  };
  const updateManual = (field: "translation" | "rotationDegrees", index: number, value: number) => {
    if (!Number.isFinite(value)) return;
    setManualAlignment((current) => {
      const next = { ...current, [field]: tupleWith(current[field], index, value) };
      if (alignmentMode === "manual") setResolvedAlignment(next);
      return next;
    });
  };
  const saveFeedback = async () => {
    const packet = feedback;
    const identity = feedbackSaveIdentity;
    if (!packet || !identity || !activeReview || !note.trim()) return;
    const sequence = ++saveSequence.current;
    saveController.current?.abort();
    const controller = new AbortController();
    saveController.current = controller;
    setFeedbackStatus({ kind: "saving", message: "Saving structured review…" });
    try {
      const receipt = await saveAirshowReviewFeedback({
        projectId: reviewProjectId,
        feedback: packet,
        signal: controller.signal,
      });
      if (controller.signal.aborted || sequence !== saveSequence.current
          || identity !== saveIdentityRef.current) return;
      setFeedbackStatus({
        kind: "success",
        message: `Saved ${receipt.filename}. Codex will read it from this project.`,
      });
    } catch (error: unknown) {
      if (controller.signal.aborted || sequence !== saveSequence.current
          || identity !== saveIdentityRef.current) return;
      setFeedbackStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to save structured review",
      });
    } finally {
      if (saveController.current === controller) saveController.current = null;
    }
  };
  const downloadFeedback = () => {
    if (!feedback || !selectedEntry || feedbackStatus.kind === "saving") return;
    const url = URL.createObjectURL(new Blob([preview], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = airshowFeedbackFilename(selectedEntry.id);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    setFeedbackStatus({ kind: "success", message: "Downloaded structured Airshow review JSON." });
  };
  const feedbackPanel = <section className="airshow-review-feedback">
    <h2>Tell Codex what is wrong</h2>
    <label><span>Review note</span><textarea data-testid="openvsp-review-text" maxLength={4_000} onChange={(event) => setNote(event.currentTarget.value)} placeholder="Example: the Loom nacelle should move aft…" rows={5} value={note} /></label>
    <details><summary>Structured message preview</summary><pre data-testid="openvsp-review-preview">{preview}</pre></details>
    <div><button aria-busy={feedbackStatus.kind === "saving"} data-testid="openvsp-review-save" disabled={!canSaveFeedback || feedbackStatus.kind === "saving"} onClick={() => void saveFeedback()} type="button">{feedbackStatus.kind === "saving" ? "Saving…" : "Save for Codex"}</button><button data-testid="openvsp-review-download" disabled={!feedback || feedbackStatus.kind === "saving"} onClick={downloadFeedback} type="button">Download JSON</button></div>
    <output aria-live="polite" data-state={feedbackStatus.kind} data-testid="openvsp-review-status">{feedbackStatus.message}</output>
  </section>;

  return (
    <main
      className="airshow-review"
      data-alignment-mode={alignmentMode}
      data-alignment-signature={alignmentSignature}
      data-camera-sync-revision={camera.revision}
      data-camera-sync-signature={camera.signature}
      data-layout={layout}
      data-overlay-opacity={overlayOpacity.toFixed(2)}
      data-testid="openvsp-comparison-review"
    >
      <header className="airshow-review-header">
        <div><span>OpenVSP Airshow · visual review</span><h1>Reference and Loom, one synchronized view</h1></div>
        <label><span>Airshow model</span><select
          data-testid="openvsp-model-select"
          disabled={!catalog}
          onChange={(event) => setEntryId(event.currentTarget.value)}
          value={selectedEntry?.id ?? entryId}
        >
          {catalog?.entries.map((entry) => <option key={entry.id} value={entry.id}>{entry.label} · {entry.archiveMemberPath}</option>)}
        </select></label>
        <div className="airshow-review-context"><span>{projectName}</span><code>{branch}</code></div>
      </header>
      {catalogState?.kind === "unavailable" ? <div className="airshow-review-alert" role="alert">Catalog unavailable: {catalogState.reason}</div> : null}
      <AirshowReviewToolNavigation
        heartbeatDisabled={!catalog}
        junctionDisabled={!junctionIdentity}
        onSelect={chooseReviewTool}
        selected={reviewTool}
      />
      <AirshowHeartbeatPanel
        active={reviewTool === "heartbeat"}
        catalog={catalog ? {
          catalogRevision: catalog.catalogRevision,
          archiveSha256: catalog.corpus.archiveSha256,
          memberCount: catalog.corpus.memberCount,
          vspGeometryCount: catalog.corpus.vspGeometryCount,
          invalidX3dCount: catalog.corpus.invalidX3dCount,
        } : null}
        onInspectModel={inspectHeartbeatModel}
        projectId={reviewProjectId}
      />
      {reviewTool === "heartbeat" ? null : reviewTool === "junction" && junctionIdentity && junctionReviewIdentity ? <AirshowJunctionLab
        feedbackPanel={feedbackPanel}
        identity={junctionIdentity}
        onEvidenceChange={(evidence) => setJunctionEvidenceState(evidence ? {
          reviewIdentity: junctionReviewIdentity,
          evidence,
        } : null)}
        projectId={reviewProjectId}
      /> : <div className="airshow-review-body">
        <section className="airshow-review-stage">
          <div className="airshow-review-toolbar">
            <div aria-label="Comparison layout" role="group">
              <button aria-pressed={layout === "split"} data-testid="openvsp-mode-side-by-side" onClick={() => setLayout("split")} type="button">Side by side</button>
              <button aria-pressed={layout === "overlay"} data-testid="openvsp-mode-overlay" onClick={() => setLayout("overlay")} type="button">Overlay</button>
            </div>
            <label><input checked={visibility.reference} data-testid="openvsp-visibility-reference" onChange={(event) => setVisibility((current) => ({ ...current, reference: event.currentTarget.checked }))} type="checkbox" />Reference</label>
            <label><input checked={visibility.loom} data-testid="openvsp-visibility-loom" onChange={(event) => setVisibility((current) => ({ ...current, loom: event.currentTarget.checked }))} type="checkbox" />Loom adapter</label>
            <label className="airshow-review-opacity"><span>Reference opacity</span><input data-testid="openvsp-overlay-opacity" max="1" min="0" onChange={(event) => setOverlayOpacity(Number(event.currentTarget.value))} step="0.05" type="range" value={overlayOpacity} /><output>{overlayOpacity.toFixed(2)}</output></label>
          </div>
          <AirshowComparisonViewer
            alignmentMode={alignmentMode}
            cameraCommand={cameraCommand}
            layout={layout}
            loom={sides.loom}
            manualAlignment={manualAlignment}
            onCameraEvidence={setCamera}
            onResolvedAlignment={updateResolvedAlignment}
            overlayOpacity={overlayOpacity}
            reference={sides.reference}
            visibility={visibility}
          />
          <footer className="airshow-review-camera">
            <span data-testid="openvsp-camera-sync">One camera · synchronized · revision {camera.revision}</span>
            <div>{(["front", "side", "top", "isometric"] as const).map((preset) => <button key={preset} onClick={() => chooseCamera(preset)} type="button">{preset}</button>)}
              <button data-testid="openvsp-camera-reset" onClick={() => chooseCamera("reset")} type="button">Reset view</button></div>
            <p>Both panels use tessellated display meshes. “Exact” refers only to attested OpenVSP control-net evidence.</p>
          </footer>
        </section>
        <aside className="airshow-review-panel">
          <section>
            <h2>Alignment</h2>
            <label><span>Comparison frame</span><select data-testid="openvsp-alignment-mode" onChange={(event) => setAlignmentMode(event.currentTarget.value as AirshowAlignmentMode)} value={alignmentMode}>
              <option value="source-frame">Source frame (identity)</option>
              <option value="bbox-center">BBox-assisted visual fit</option>
              <option value="manual">Manual visual alignment</option>
            </select></label>
            {alignmentMode === "manual" ? <div className="airshow-review-manual">
              {(["x", "y", "z"] as const).map((axis, index) => <label key={`t-${axis}`}><span>Translate {axis.toUpperCase()}</span><input data-testid={`openvsp-alignment-translate-${axis}`} onChange={(event) => updateManual("translation", index, Number(event.currentTarget.value))} step="0.05" type="number" value={manualAlignment.translation[index]} /></label>)}
              {(["x", "y", "z"] as const).map((axis, index) => <label key={`r-${axis}`}><span>Rotate {axis.toUpperCase()}°</span><input data-testid={`openvsp-alignment-rotate-${axis}`} onChange={(event) => updateManual("rotationDegrees", index, Number(event.currentTarget.value))} step="0.5" type="number" value={manualAlignment.rotationDegrees[index]} /></label>)}
              <label><span>Uniform scale</span><input data-testid="openvsp-alignment-scale" min="0.001" onChange={(event) => {
                const scale = Number(event.currentTarget.value);
                if (Number.isFinite(scale) && scale > 0) {
                  const next = { ...manualAlignment, scale };
                  setManualAlignment(next); setResolvedAlignment(next);
                }
              }} step="0.01" type="number" value={manualAlignment.scale} /></label>
            </div> : null}
            <button data-testid="openvsp-alignment-reset" onClick={resetAlignment} type="button">Reset to source frame</button>
            <code>{alignmentSignature}</code>
            {alignmentMode !== "source-frame" ? <p>Assisted/manual alignment is visual only; quantitative comparison is disabled in the review packet.</p> : null}
          </section>
          <section className="airshow-review-evidence">
            <h2>Evidence</h2>
            <dl><div><dt>Reference</dt><dd>{sides.reference.fidelityLabel}</dd></div><div><dt>Loom OpenVSP adapter</dt><dd>{sides.loom.fidelityLabel}</dd></div><div><dt>Requested sampling</dt><dd>8</dd></div></dl>
            {activeError ? <p className="airshow-review-alert" role="alert">{activeError}</p> : null}
          </section>
          {feedbackPanel}
        </aside>
      </div>}
    </main>
  );
}
